
import { defineComponent, onBeforeMount, reactive } from 'vue'

// > components <
import { BsPage } from '@/modern/components'

// > submodules/waybills/components
import {
    WaybillsHeaderAdditional,
    WaybillsFilters,
    WaybillsTableColumnNumber,
    WaybillsTableColumnCalculationType,
    WaybillsTableColumnType,
    WaybillsTableColumnForwarder,
    WaybillsTableColumnCourier,
    WaybillsTableColumnCourierPaymentRateType,
    WaybillsTableColumnCar,
    WaybillsTableColumnCarNumber,
    WaybillsTableColumnDate,
    WaybillsTableColumnInvoices,
    WaybillsTableColumnAsMoney,
    WaybillsTableColumnMoney,
    WaybillsTableColumnStatus,
    WaybillsTableColumnActions,
    WaybillDetailsModal,
    WaybillRequestForPaymentModal
} from '@/modern/modules/m-dispatcher-module/submodules/waybills/components'

// > services/types <
import { Waybill, GetWaybillParamsFilters } from '@/modern/modules/m-dispatcher-module/services/types/waybill.types'

// > services/waybills
import waybillsAPI from '@/modern/modules/m-dispatcher-module/services/waybill.service'

// > helpers/saveFile <
import saveFile from '@/modern/helpers/saveFile'

const COLUMNS = [
    {
        key: 'codeOneC',
        name: 'Номер'    
    },
    {
        key: 'city',
        name: 'Город'
    },
    {
        key: 'waybillType',
        name: 'Тип'
    },
    {
        key: 'company',
        name: 'Перевозчик'
    },
    {
        key: 'calculationType',
        name: 'Тип расчёта'
    },
    {
        key: 'courierPaymentRateType',
        name: 'Ставка'
    },
    {
        key: 'courier',
        name: 'Водитель'
    },
    {
        key: 'forwarder',
        name: 'Экипаж'
    },
    {
        key: 'car',
        name: 'Транспорт'
    },
    {
        key: 'carNumber',
        name: 'Гос. номер'
    },
    {
        key: 'createdAt',
        name: 'Дата'
    },
    {
        key: 'invoices',
        name: 'Накладных'
    },
    {
        key: 'additionalServicesTotalAmount',
        name: 'Доп. услуги'
    },
    {
        key: 'totalAmount',
        name: 'Сумма'
    },
    {
        key: 'status',
        name: 'Статус'
    }
]

export default defineComponent({
    components: {
        BsPage,
        WaybillsHeaderAdditional,
        WaybillsFilters,
        WaybillsTableColumnNumber,
        WaybillsTableColumnCalculationType,
        WaybillsTableColumnType,
        WaybillsTableColumnForwarder,
        WaybillsTableColumnCourier,
        WaybillsTableColumnCourierPaymentRateType,
        WaybillsTableColumnCar,
        WaybillsTableColumnCarNumber,
        WaybillsTableColumnDate,
        WaybillsTableColumnInvoices,
        WaybillsTableColumnAsMoney,
        WaybillsTableColumnMoney,
        WaybillsTableColumnStatus,
        WaybillsTableColumnActions,
        WaybillDetailsModal,
        WaybillRequestForPaymentModal,
    },
    setup() {
        const state = reactive({
            isLoading: false,
            isFailed: false,
            list: [] as Array<Waybill>,
            limit: 50,
            columns: [...COLUMNS] as Array<{ key: string; name: string }>,
            pagination: {
                page: 1,
                totalPages: 1,
                totalItems: 0
            },
            filters: {
                dateFrom: null as string | null,
                dateTo: null as string | null,
                codeOneC: null as string | null,
                companyId: null as number | null,
                courierId: null as number | null,
                statusId: null as number | null,
                paymentTypeId: null as number | null,
                waybillTypeId: null as number | null,
                cityId: null as number | null,
            }
        })

        const statisticState = reactive({
            isLoading: false,
            cargoWaybills: 0,
            crewWaybills: 0
        })

        const exportState = reactive({
            isLoading: false,
        })

        const detailsState = reactive({
            isOpened: false,
            waybillId: null as number | null,
        })

        const requestForPaymentState = reactive({
            isOpened: false,
            waybill: null as Waybill | null
        })

        const loadWaybills = async (page: number) => {
            state.pagination.page = page

            try {
                state.isLoading = true

                const response = await waybillsAPI.getWaybills({
                    page: state.pagination.page,
                    limit: state.limit,
                    dateFrom: state.filters.dateFrom,
                    dateTo: state.filters.dateTo,
                    codeOneC: state.filters.codeOneC,
                    companyId: state.filters.companyId,
                    courierId: state.filters.courierId,
                    statusId: state.filters.statusId,
                    paymentTypeId: state.filters.paymentTypeId,
                    waybillTypeId: state.filters.waybillTypeId,
                    cityId: state.filters.cityId
                })

                state.list = response.data.data
                state.pagination.totalPages = response.data.meta.last_page
                state.pagination.totalItems = response.data.meta.total

                state.isFailed = false
            } catch (error) {
                console.error(error)
                state.isFailed = true
            } finally {
                state.isLoading = false
            }
        }

        const loadWaybillsStatistic = async () => {
            try {
                statisticState.isLoading = true

                const response = await waybillsAPI.getWaybillsStatistic()

                statisticState.cargoWaybills = response.data.data.cargoWaybills
                statisticState.crewWaybills = response.data.data.crewWaybills
            } catch (error) {
                console.error(error)
            } finally {
                statisticState.isLoading = false
            }
        }

        onBeforeMount(() => {
            loadWaybills(1)
            loadWaybillsStatistic()
        })

        const onFiltersSubmit = (params: GetWaybillParamsFilters) => {
            state.filters.dateFrom = params.dateFrom as string | null
            state.filters.dateTo = params.dateTo as string | null
            state.filters.codeOneC = params.codeOneC as string | null
            state.filters.companyId = params.companyId as number | null
            state.filters.courierId = params.courierId as number | null
            state.filters.statusId = params.statusId as number | null
            state.filters.paymentTypeId = params.paymentTypeId as number | null
            state.filters.waybillTypeId = params.waybillTypeId as number | null
            state.filters.cityId = params.cityId as number | null

            loadWaybills(1)
        }

        const onFiltersReset = () => {
            state.filters.dateFrom = null
            state.filters.dateTo = null
            state.filters.codeOneC = null
            state.filters.companyId = null
            state.filters.courierId = null
            state.filters.statusId = null
            state.filters.paymentTypeId = null
            state.filters.waybillTypeId = null
            state.filters.cityId = null

            loadWaybills(1)
        }

        const onOpenWaybillDetails = (waybillId: number) => {
            detailsState.waybillId = waybillId
            detailsState.isOpened = true
        }

        const onCloseWaybillDetails = () => {
            detailsState.isOpened = false
            detailsState.waybillId = null
        }

        const onExportWaybills = async () => {
            try {
                exportState.isLoading = true

                const response = await waybillsAPI.exportWaybills({
                    page: state.pagination.page,
                    limit: state.limit,
                    dateFrom: state.filters.dateFrom,
                    dateTo: state.filters.dateTo,
                    codeOneC: state.filters.codeOneC,
                    companyId: state.filters.companyId,
                    courierId: state.filters.courierId,
                    statusId: state.filters.statusId,
                    paymentTypeId: state.filters.paymentTypeId,
                    waybillTypeId: state.filters.waybillTypeId,
                    cityId: state.filters.cityId
                })

                saveFile(response.data, 'Путевые листы')
            } catch (error) {
                console.error(error)
            } finally {
                exportState.isLoading = false
            }
        }

        const onOpenWaybillRequestForPayment = (waybill: Waybill) => {
            requestForPaymentState.waybill = { ...waybill }
            requestForPaymentState.isOpened = true
        }

        const onCloseWaybillequestForPayment = () => {
            requestForPaymentState.isOpened = false
            requestForPaymentState.waybill = null
        }

        return {
            state,
            statisticState,
            detailsState,
            exportState,
            requestForPaymentState,
            COLUMNS,
            loadWaybills,
            loadWaybillsStatistic,
            onFiltersSubmit,
            onFiltersReset,
            onOpenWaybillDetails,
            onCloseWaybillDetails,
            onExportWaybills,
            onOpenWaybillRequestForPayment,
            onCloseWaybillequestForPayment
        }
    }
})
